import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Hd as HdIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface Format {
  format_id: string;
  type: string;
  quality: string;
  qualityLabel: string;
  resolution: string;
  container: string;
  hasAudio: boolean;
  hasVideo: boolean;
  filesize: string;
  url: string;
  format_note?: string;
}

interface FormatListProps {
  formats: Format[];
}

const FormatList = ({ formats }: FormatListProps) => {
  const [activeTab, setActiveTab] = useState(0);

  const combinedFormats = formats.filter((f) => f.hasAudio && f.hasVideo);
  const videoOnlyFormats = formats.filter((f) => f.hasVideo && !f.hasAudio);
  const audioOnlyFormats = formats.filter((f) => f.hasAudio && !f.hasVideo);

  const formatGroups = [
    { label: 'Combined', formats: combinedFormats },
    { label: 'Video Only', formats: videoOnlyFormats },
    { label: 'Audio Only', formats: audioOnlyFormats },
  ];

  const handleDownload = (url: string, format: Format) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `video.${format.container}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': {
            background: 'linear-gradient(45deg, #00ff9d 30%, #ff0099 90%)',
          },
        }}
      >
        {formatGroups.map((group, index) => (
          <Tab
            key={group.label}
            label={`${group.label} (${group.formats.length})`}
            sx={{
              '&.Mui-selected': {
                color: 'primary.main',
              },
            }}
          />
        ))}
      </Tabs>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        key={activeTab}
      >
        <Grid container spacing={2}>
          {formatGroups[activeTab].formats.map((format) => (
            <Grid item xs={12} key={format.format_id}>
              <motion.div variants={item}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {format.hasVideo ? (
                      format.hasAudio ? (
                        <HdIcon sx={{ color: 'primary.main' }} />
                      ) : (
                        <VideoIcon sx={{ color: 'secondary.main' }} />
                      )
                    ) : (
                      <AudioIcon sx={{ color: '#ff9900' }} />
                    )}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {format.qualityLabel || format.quality}{' '}
                        {format.resolution && `(${format.resolution})`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={format.container.toUpperCase()}
                          size="small"
                          sx={{ bgcolor: 'background.paper' }}
                        />
                        {format.filesize && (
                          <Chip
                            label={
                              typeof format.filesize === 'number'
                                ? `${(format.filesize / 1024 / 1024).toFixed(1)} MB`
                                : format.filesize
                            }
                            size="small"
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(format.url, format)}
                    sx={{
                      background: 'linear-gradient(45deg, #00ff9d 30%, #ff0099 90%)',
                      color: 'background.paper',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #00ff9d 20%, #ff0099 100%)',
                        transform: 'scale(1.02)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Download
                  </Button>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Paper>
  );
};

export default FormatList; 